import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Pause, Play, X, Check, StopCircle } from "lucide-react";
import { playCompletionSound } from "@/lib/sound";
import { useTheme } from "@/context/ThemeContext";
import { useMusicPlayer, MiniPlayer } from "@/components/ascend/MusicPlayer";

const TOTAL_SECONDS = 45 * 60;

export default function FocusModal({ session, northStar, onFinish }) {
  const [remaining, setRemaining] = useState(TOTAL_SECONDS);
  const [paused, setPaused] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const intervalRef = useRef(null);
  const { theme } = useTheme();
  const musicEnabled = theme?.music_enabled_during_focus;
  const musicPlayer = useMusicPlayer({ initialVolume: theme?.music_default_volume ?? 30 });

  // Auto-start music once tracks load
  useEffect(() => {
    if (!musicEnabled || !musicPlayer.tracks.length) return;
    const defaultId = theme?.music_default_track_id;
    const idx = defaultId ? musicPlayer.tracks.findIndex((t) => t.id === defaultId) : 0;
    musicPlayer.playTrackByIndex(idx >= 0 ? idx : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [musicPlayer.tracks.length, musicEnabled]);

  useEffect(() => {
    if (paused) return;
    intervalRef.current = setInterval(() => {
      setRemaining((s) => {
        if (s <= 1) {
          clearInterval(intervalRef.current);
          void handleComplete();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused]);

  // Mirror the countdown into document.title so she can spot the timer from
  // any other tab. Scoped strictly to this component's lifecycle.
  useEffect(() => {
    const m = Math.floor(remaining / 60);
    const s = String(remaining % 60).padStart(2, "0");
    const label = paused ? "paused" : "focus";
    document.title = `cooking — ${label} ${m}:${s}`;
  }, [remaining, paused]);

  // Restore the plain tab title exactly once on unmount (modal close / finish /
  // navigate away). Empty deps so the cleanup only fires when the component
  // actually goes away — avoids flicker between ticks.
  useEffect(() => {
    return () => {
      document.title = "cooking";
    };
  }, []);

  const elapsed = TOTAL_SECONDS - remaining;
  const minutesElapsed = Math.floor(elapsed / 60);

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const pct = (elapsed / TOTAL_SECONDS) * 100;

  async function handleComplete() {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/focus/${session.session_id}/complete`);
      playCompletionSound(theme?.sound_choice || "silent");
      if (musicEnabled) musicPlayer.pauseFade(2000);
      toast.success("Beautifully done — 45 minutes logged.");
      onFinish?.({ block_id: session.block_id, completed: true });
    } catch (e) {
      toast.error(formatApiError(e));
      onFinish?.({ block_id: session.block_id, completed: false });
    } finally {
      setBusy(false);
    }
  }

  async function handleEndEarly() {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/focus/${session.session_id}/end`, {
        minutes_elapsed: minutesElapsed,
      });
      if (data.discarded) {
        toast("Too short to log — nothing saved. Come back when you're ready.");
      } else {
        toast.success(`Logged ${data.minutes_logged} minutes.`);
      }
      onFinish?.({ block_id: session.block_id, completed: false });
    } catch (e) {
      toast.error(formatApiError(e));
      onFinish?.({ block_id: session.block_id, completed: false });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center px-6"
      style={{
        background: "linear-gradient(180deg, var(--bg) 0%, var(--bg-2) 100%)",
      }}
      data-testid="focus-modal"
    >
      {/* Subject */}
      <div className="text-center fade-up">
        <span className="eyebrow">focus, gently</span>
        <h2 className="font-display text-5xl sm:text-6xl mt-1" style={{ color: session.subject_color || "var(--primary)" }} data-testid="focus-subject">
          {session.subject_name}
        </h2>
      </div>

      {/* Timer ring */}
      <div className="my-10 relative fade-up delay-1" data-testid="focus-timer">
        <svg width="260" height="260" viewBox="0 0 260 260" className="-rotate-90">
          <circle cx="130" cy="130" r="118" strokeWidth="6" fill="none"
                  stroke={session.subject_color
                    ? `color-mix(in oklab, ${session.subject_color} 25%, transparent)`
                    : "color-mix(in oklab, var(--primary-soft) 60%, transparent)"} />
          <circle cx="130" cy="130" r="118" strokeWidth="6" fill="none"
                  stroke={session.subject_color || "var(--primary)"}
                  strokeLinecap="round"
                  strokeDasharray={`${(pct * 741.4) / 100} 741.4`}
                  style={{ transition: "stroke-dasharray 1s linear" }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="font-display text-6xl sm:text-7xl tabular-nums" style={{ color: "var(--text)" }}>
            {mm}:{ss}
          </div>
          <div className="font-hand text-lg mt-1" style={{ color: "var(--accent)" }}>
            {paused ? "paused" : "keep going"}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 fade-up delay-2">
        <button
          className="btn-ghost flex items-center gap-2"
          onClick={() => setPaused((p) => !p)}
          disabled={busy || remaining === 0}
          data-testid="focus-pause-btn"
        >
          {paused ? <Play size={14} strokeWidth={1.5} /> : <Pause size={14} strokeWidth={1.5} />}
          {paused ? "Resume" : "Pause"}
        </button>
        <button
          className="btn-primary flex items-center gap-2"
          onClick={handleComplete}
          disabled={busy}
          data-testid="focus-complete-btn"
        >
          <Check size={14} strokeWidth={1.75} /> Complete
        </button>
        {!confirmEnd ? (
          <button
            className="btn-ghost flex items-center gap-2 text-sm"
            onClick={() => setConfirmEnd(true)}
            disabled={busy}
            data-testid="focus-end-btn"
          >
            <StopCircle size={14} strokeWidth={1.5} /> End early
          </button>
        ) : (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted">Are you sure?</span>
            <button className="btn-ghost !py-1 !px-3 text-sm" onClick={() => setConfirmEnd(false)} data-testid="focus-end-cancel">Keep going</button>
            <button className="btn-primary !py-1 !px-3 text-sm" onClick={handleEndEarly} data-testid="focus-end-confirm">Yes, end</button>
          </div>
        )}
      </div>

      {/* North star */}
      {northStar && (
        <div className="mt-14 max-w-2xl text-center fade-up delay-3">
          <div className="eyebrow mb-1">remember</div>
          <div className="font-display italic text-xl sm:text-2xl leading-snug" style={{ color: "var(--text-muted)" }}>
            "{northStar}"
          </div>
        </div>
      )}

      {musicEnabled && (
        <div className="fixed bottom-4 right-4 w-72 max-w-[92vw] z-[70]">
          <MiniPlayer player={musicPlayer} />
        </div>
      )}
    </div>
  );
}
